import { useCallback, useEffect, useState } from "react";

interface Worker {
  id: string;
  displayName: string;
  hostname: string;
  state: string;
  specialty: string;
  platform: string | null;
  cpuCount: number | null;
  totalMemoryMb: number | null;
  capabilities: string[];
  activeJobCount: number;
  maxConcurrentJobs?: number;
  lastHeartbeatAt: string | null;
  jobsCompleted: number;
  jobsFailed: number;
  /** Opcional: un servidor anterior a la Fase 4 no lo manda, y no se rompe. */
  panel?: Panel;
}

export interface Panel {
  ahora: {
    jobId: string;
    titulo: string;
    paso: string | null;
    hechos: number | null;
    deCuantos: number | null;
    desde: string | null;
  }[];
  cola: { suyos: { jobId: string; titulo: string; desde: string | null }[]; libres: number };
  cuota: {
    aLaVez: number;
    maximo: number;
    hoy: { hechos: number; fallidos: number };
    /** Null: la máquina todavía no informa de sus agentes (un worker de antes de la Fase 5). */
    agentes: AgenteEnElPanel[] | null;
  };
  herramientas: { nombre: string; version: string | null; disponible: boolean }[];
}

/** Un agente de programación en la ficha de su máquina (Fase 5). */
export interface AgenteEnElPanel {
  agente: string;
  nivel: "verde" | "ambar" | "rojo";
  apto: boolean;
  porque: string | null;
  plan: string | null;
  version: string | null;
  ventanas: { tipo: string; usado: number | null; reiniciaEn: string | null }[];
  vuelveA: string | null;
  medidoEn: string;
}

/** La API para los agentes (Fase 5). Hoy: ninguna capacidad, y sin ejecutor. */
export interface ApiDeAgentes {
  permitidas: { capacidad: string; topeEurosDia: number }[];
  ejecutor: boolean;
}

interface TrabajoHecho {
  jobId: string;
  titulo: string;
  capacidad: string;
  estado: string;
  empezo: string | null;
  termino: string | null;
  duracionMs: number | null;
  error: string | null;
}

interface MesResumido {
  mes: string;
  capacidad: string;
  hechos: number;
  fallidos: number;
  otros: number;
  duracionMsMedia: number | null;
  duracionMsMax: number | null;
}

/** «45 s», «3 min», «1 h 5 min». Se lee de un vistazo; los milisegundos no. */
function duracion(ms: number | null): string {
  if (ms === null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

const ESTADO_HECHO: Record<string, string> = {
  succeeded: "hecho",
  failed: "falló",
  cancelled: "cancelado",
  dead: "abandonado",
};

const ESPECIALIDAD: Record<string, string> = {
  development: "desarrollo",
  seo: "SEO",
  design: "diseño",
  operations: "operaciones",
  research: "investigación",
  general: "general",
};

function haceCuanto(iso: string | null): string {
  if (!iso) return "nunca";
  const segundos = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (segundos < 60) return `hace ${segundos} s`;
  if (segundos < 3600) return `hace ${Math.round(segundos / 60)} min`;
  if (segundos < 86400) return `hace ${Math.round(segundos / 3600)} h`;
  return `hace ${Math.round(segundos / 86400)} días`;
}

/** Lo que queda de vida al código de alta, que dura una hora. */
function caducaEn(iso: string): string {
  const minutos = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  if (minutos <= 0) return "ya ha caducado";
  if (minutos === 1) return "caduca en 1 minuto";
  return `caduca en ${minutos} minutos`;
}

interface Alta {
  token: string;
  expiresAt: string;
}

/**
 * Dar de alta un ordenador nuevo.
 *
 * El orden de la pantalla es el de los pasos: primero el instalador, luego el
 * código. Al revés se genera un código que caduca en una hora mientras buscas
 * dónde se descargaba el programa.
 */
/*
 * Se exporta para poder usarlo solo.
 *
 * En el Panel de Control ya hay un apartado que enseña las maquinas —con su
 * ultimo latido, sus nucleos y lo que saben hacer—, asi que alli solo hace
 * falta el boton. Repetir la lista debajo seria enseñar dos veces lo mismo
 * con dos formatos distintos, que es como se dejan de mirar los dos.
 */
export function AnadirOrdenador({ alTerminar }: { alTerminar?: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState("");
  const [especialidad, setEspecialidad] = useState("general");
  const [alta, setAlta] = useState<Alta | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  const generar = async () => {
    setOcupado(true);
    setError(null);
    try {
      const r = await fetch("/api/workers/pairing-token", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ displayName: nombre.trim(), specialty: especialidad }),
      });
      const cuerpo = (await r.json()) as Alta & { error?: string };
      if (!r.ok) throw new Error(cuerpo.error ?? `Error ${r.status}`);
      setAlta(cuerpo);
      alTerminar?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se ha podido generar el código");
    } finally {
      setOcupado(false);
    }
  };

  if (!abierto) {
    return (
      <div className="anadir-maquina">
        <button type="button" className="principal" onClick={() => setAbierto(true)}>
          Añadir un ordenador
        </button>
      </div>
    );
  }

  return (
    <section className="anadir-maquina abierta">
      <header>
        <strong>Añadir un ordenador</strong>
        <button
          type="button"
          className="cerrar"
          onClick={() => {
            setAbierto(false);
            setAlta(null);
            setNombre("");
          }}
        >
          Cerrar
        </button>
      </header>

      <ol className="pasos-alta">
        <li>
          <p>
            <strong>Descarga el instalador</strong> y llévalo a ese ordenador.
          </p>
          <a className="descarga" href="/descargas/instalar-worker.cmd" download>
            instalar-worker.cmd
          </a>
          <p className="nota">
            Doble clic y él solo instala todo, incluidos <code>claude</code> y{" "}
            <code>codex</code>. Necesita Node; si falta, te lo dice y abre la página de descarga.
          </p>
        </li>

        <li>
          <p>
            <strong>Genera el código</strong> con el ordenador ya delante: dura una hora.
          </p>
          {!alta ? (
            <div className="formulario-alta">
              <label>
                Cómo se va a llamar
                <input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="PC del salón"
                  maxLength={80}
                />
              </label>
              <label>
                Para qué
                <select value={especialidad} onChange={(e) => setEspecialidad(e.target.value)}>
                  {Object.entries(ESPECIALIDAD).map(([valor, texto]) => (
                    <option key={valor} value={valor}>
                      {texto}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="principal"
                disabled={ocupado || nombre.trim().length === 0}
                onClick={() => void generar()}
              >
                {ocupado ? "Generando…" : "Generar código"}
              </button>
              {error && <p className="error">{error}</p>}
            </div>
          ) : (
            <div className="codigo-alta">
              <code>{alta.token}</code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(alta.token);
                  setCopiado(true);
                  setTimeout(() => setCopiado(false), 2000);
                }}
              >
                {copiado ? "Copiado" : "Copiar"}
              </button>
              <span className="nota">{caducaEn(alta.expiresAt)}</span>
            </div>
          )}
        </li>

        <li>
          <p>
            <strong>Pega el código</strong> cuando el instalador te lo pida.
          </p>
          <p className="nota">
            El ordenador aparecerá aquí abajo en unos segundos, y arrancará solo cada vez que se
            encienda.
          </p>
        </li>
      </ol>
    </section>
  );
}

/**
 * Lo que ha hecho una máquina. Plegado, y se pide al abrirlo.
 *
 * Hasta la Fase 4 no había forma de preguntar «¿qué hizo ayer el portátil?»:
 * existía un trabajo suelto por id y la lista de lo que estaba en marcha. Los
 * noventa días en crudo van aquí; lo anterior, resumido por mes debajo.
 */
export function Historial({ workerId }: { workerId: string }) {
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState<"" | "succeeded" | "failed">("");
  const [trabajos, setTrabajos] = useState<TrabajoHecho[] | null>(null);
  const [hayMas, setHayMas] = useState(false);
  const [meses, setMeses] = useState<MesResumido[]>([]);

  useEffect(() => {
    if (!abierto) return;
    let sigue = true;
    const q = new URLSearchParams({ limite: "30", ...(estado ? { estado } : {}) });
    void Promise.all([
      fetch(`/api/workers/${workerId}/trabajos?${q.toString()}`, { credentials: "same-origin" }).then(
        (r) => (r.ok ? r.json() : { trabajos: [], hayMas: false }),
      ),
      fetch(`/api/workers/${workerId}/meses`, { credentials: "same-origin" }).then((r) =>
        r.ok ? r.json() : { meses: [] },
      ),
    ]).then(([t, m]) => {
      if (!sigue) return;
      setTrabajos((t as { trabajos: TrabajoHecho[] }).trabajos);
      setHayMas((t as { hayMas: boolean }).hayMas);
      setMeses((m as { meses: MesResumido[] }).meses);
    });
    return () => {
      sigue = false;
    };
  }, [abierto, estado, workerId]);

  return (
    <details
      className="historial"
      onToggle={(e) => setAbierto((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>Lo que ha hecho</summary>

      <label className="filtro">
        Mostrar{" "}
        <select value={estado} onChange={(e) => setEstado(e.target.value as typeof estado)}>
          <option value="">todo</option>
          <option value="succeeded">sólo lo hecho</option>
          <option value="failed">sólo lo que falló</option>
        </select>
      </label>

      {trabajos === null ? (
        <p className="nada">Cargando…</p>
      ) : trabajos.length === 0 ? (
        <p className="nada">Nada en los últimos noventa días.</p>
      ) : (
        <ul className="hechos">
          {trabajos.map((t) => (
            <li key={t.jobId} className={t.estado === "failed" ? "fallo" : undefined}>
              <span className="que">{t.titulo}</span>
              <span className="cuando">
                {ESTADO_HECHO[t.estado] ?? t.estado} · {haceCuanto(t.termino)} · {duracion(t.duracionMs)}
              </span>
              {t.error && <span className="por-que">{t.error}</span>}
            </li>
          ))}
          {hayMas && <li className="nada">Hay más: filtra por lo hecho o lo que falló.</li>}
        </ul>
      )}

      {meses.length > 0 && (
        <table className="meses">
          <caption>Antes, resumido por mes</caption>
          <thead>
            <tr>
              <th scope="col">Mes</th>
              <th scope="col">Qué</th>
              <th scope="col">Hechos</th>
              <th scope="col">Fallidos</th>
              <th scope="col">Media</th>
            </tr>
          </thead>
          <tbody>
            {meses.map((m) => (
              <tr key={`${m.mes}-${m.capacidad}`}>
                <td>{m.mes}</td>
                <td>{m.capacidad}</td>
                <td>{m.hechos}</td>
                <td>{m.fallidos}</td>
                <td>{duracion(m.duracionMsMedia)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  );
}

const NOMBRE_DE_AGENTE: Record<string, string> = { claude: "Claude Code", codex: "Codex" };

const NOMBRE_DE_VENTANA: Record<string, string> = {
  sesion: "sesión",
  semana: "semana",
  semana_opus: "semana Opus",
  semana_sonnet: "semana Sonnet",
};

/** «a las 18:00» si es hoy; «lun 20:24» esta semana; «17 sept 12:02» más allá. */
function cuandoVuelve(iso: string): string {
  const d = new Date(iso);
  const ahora = new Date();
  const hora = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === ahora.toDateString()) return `a las ${hora}`;
  const dias = (d.getTime() - ahora.getTime()) / 86_400_000;
  if (dias > 0 && dias < 6) return `${d.toLocaleDateString("es-ES", { weekday: "short" })} ${hora}`;
  return `${d.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} ${hora}`;
}

/**
 * Una línea por agente: cómo está, su plan, cuánto le queda y cuándo se midió.
 *
 * El color dice qué hacer. Rojo: no se le manda nada —sin sesión, clave de API
 * o uso extra de pago—. Ámbar: límite alcanzado, o por encima del 80 %. Verde:
 * trabaja. La edad siempre: un dato de hace tres horas no se puede leer como
 * uno de ahora.
 */
function LineaDeAgente({ a }: { a: AgenteEnElPanel }) {
  const usos = a.ventanas
    .filter((v) => v.usado !== null)
    .map((v) => `${NOMBRE_DE_VENTANA[v.tipo] ?? v.tipo.replace(/_/g, " ")} ${Math.round(v.usado ?? 0)} %`);
  const masGastada = a.ventanas
    .filter((v) => v.usado !== null && v.reiniciaEn)
    .sort((x, y) => (y.usado ?? 0) - (x.usado ?? 0))[0];
  return (
    <li className={`agente ${a.nivel}`}>
      <span className="nombre">{NOMBRE_DE_AGENTE[a.agente] ?? a.agente}</span>
      {a.plan && <span className="plan">{a.plan}</span>}
      {a.nivel === "rojo" ? (
        <span className="porque">{a.porque}</span>
      ) : (
        <>
          {a.porque && (
            <span className="porque">
              {a.porque}
              {a.vuelveA ? ` · vuelve ${cuandoVuelve(a.vuelveA)}` : ""}
            </span>
          )}
          {usos.length > 0 && (
            <span className="uso">
              {usos.join(" · ")}
              {masGastada?.reiniciaEn ? ` · se reinicia ${cuandoVuelve(masGastada.reiniciaEn)}` : ""}
            </span>
          )}
        </>
      )}
      <span className="edad">medido {haceCuanto(a.medidoEn)}</span>
    </li>
  );
}

/**
 * Lo que la Fase 4 añade a la ficha de una máquina, y que no se veía en ningún
 * sitio: qué le espera, cuánto lleva hoy, el hueco de los límites de los
 * agentes, y con qué herramientas trabaja.
 *
 * Va aparte porque se pinta dentro de la ficha de Control, que ya enseña el
 * trabajo en marcha, el latido y las capacidades. Repetir aquí esas tres cosas
 * sería enseñar lo mismo dos veces con dos formatos, que es como se deja de
 * mirar las dos.
 */
export function ExtrasDeMaquina({ panel }: { panel: Panel | undefined }) {
  if (!panel) return null;
  const { cola, cuota, herramientas } = panel;
  return (
    <dl className="detalle extras">
      <div>
        <dt>En cola</dt>
        <dd>
          {cola.suyos.length === 0 && cola.libres === 0 ? (
            "nada esperando"
          ) : (
            <>
              {cola.suyos.length > 0 && (
                <ul className="cola">
                  {cola.suyos.map((c) => (
                    <li key={c.jobId}>
                      <span className="que">{c.titulo}</span>
                      <span className="cuando">encargado {haceCuanto(c.desde)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {cola.libres > 0 && (
                <span className="libres">y {cola.libres} sin asignar que podría coger</span>
              )}
            </>
          )}
        </dd>
      </div>
      <div>
        <dt>Hoy</dt>
        <dd>
          <span className="cuota">
            {cuota.hoy.hechos} {cuota.hoy.hechos === 1 ? "hecho" : "hechos"}
            {cuota.hoy.fallidos > 0 && (
              <span className="fallo">
                , {cuota.hoy.fallidos} {cuota.hoy.fallidos === 1 ? "falló" : "fallaron"}
              </span>
            )}
          </span>
        </dd>
      </div>
      {cuota.agentes === null ? (
        <div>
          <dt>Agentes</dt>
          <dd className="sin-agentes">esta máquina todavía no informa de sus agentes</dd>
        </div>
      ) : (
        cuota.agentes.length > 0 && (
          <div>
            <dt>Agentes</dt>
            <dd>
              <ul className="agentes">
                {cuota.agentes.map((a) => (
                  <LineaDeAgente key={a.agente} a={a} />
                ))}
              </ul>
            </dd>
          </div>
        )
      )}
      {herramientas.length > 0 && (
        <div>
          <dt>Herramientas</dt>
          <dd className="herramientas">
            {herramientas
              .map(
                (h) =>
                  `${h.nombre}${h.version ? ` ${h.version}` : ""}${h.disponible ? "" : " (no disponible)"}`,
              )
              .join(" · ")}
          </dd>
        </div>
      )}
    </dl>
  );
}

export function Workers() {
  const [workers, setWorkers] = useState<Worker[] | null>(null);

  const cargar = useCallback(async () => {
    const r = await fetch("/api/workers", { credentials: "same-origin" });
    if (r.ok) setWorkers(((await r.json()) as { workers: Worker[] }).workers);
  }, []);

  useEffect(() => {
    void cargar();
    // Se refresca solo: un panel de estado que hay que recargar a mano
    // enseña el pasado, no el presente.
    const t = setInterval(() => void cargar(), 5_000);
    return () => clearInterval(t);
  }, [cargar]);

  if (!workers) return <div className="provisional">Cargando…</div>;

  // El botón de añadir va SIEMPRE, también cuando no hay ninguno: es
  // justamente cuando más falta hace.
  if (workers.length === 0) {
    return (
      <div className="panel">
        <AnadirOrdenador alTerminar={() => void cargar()} />
        <div className="provisional">
          <p>
            Todavía no hay ningún ordenador dado de alta.
            <br />
            BilboBot puede pensar y planificar, pero no ejecutar nada.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <AnadirOrdenador alTerminar={() => void cargar()} />
      {workers.map((w) => {
        const conectado = w.state !== "offline";
        const p = w.panel;
        return (
          <article key={w.id} className={`maquina ${conectado ? "viva" : "muerta"}`}>
            <header>
              <span className={`punto ${conectado ? "verde" : "gris"}`} />
              <strong>{w.displayName}</strong>
              <span className="etiqueta">{ESPECIALIDAD[w.specialty] ?? w.specialty}</span>
            </header>

            <dl>
              <div>
                <dt>Ahora</dt>
                <dd>
                  {!conectado ? (
                    "apagado"
                  ) : !p || p.ahora.length === 0 ? (
                    "disponible, sin nada en marcha"
                  ) : (
                    <ul className="ahora">
                      {p.ahora.map((a) => {
                        const parte =
                          a.hechos !== null && a.deCuantos ? Math.min(100, (a.hechos / a.deCuantos) * 100) : null;
                        return (
                          <li key={a.jobId}>
                            <span className="que">{a.titulo}</span>
                            <span className="cuando">
                              {a.paso ?? "trabajando"}
                              {a.hechos !== null && a.deCuantos ? ` · ${a.hechos} de ${a.deCuantos}` : ""} ·
                              desde {haceCuanto(a.desde)}
                            </span>
                            {parte !== null && (
                              <span className="progreso" role="progressbar" aria-valuenow={Math.round(parte)} aria-valuemin={0} aria-valuemax={100}>
                                <span style={{ width: `${parte}%` }} />
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </dd>
              </div>

              <div>
                <dt>Sabe hacer</dt>
                <dd>
                  {w.capabilities.join(", ") || "nada declarado"}
                  {p && p.herramientas.length > 0 && (
                    <span className="herramientas">
                      {p.herramientas
                        .map((h) => `${h.nombre}${h.version ? ` ${h.version}` : ""}${h.disponible ? "" : " (no disponible)"}`)
                        .join(" · ")}
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt>Máquina</dt>
                <dd>
                  {w.hostname} · {w.platform} · {w.cpuCount} núcleos ·{" "}
                  {Math.round((w.totalMemoryMb ?? 0) / 1024)} GB · último latido {haceCuanto(w.lastHeartbeatAt)}
                </dd>
              </div>
            </dl>

            <ExtrasDeMaquina panel={p} />
            <Historial workerId={w.id} />
          </article>
        );
      })}
    </div>
  );
}
